/**
 * Live Invalidation Module
 * 
 * This module handles real-time cache invalidation events from the server.
 * It uses the centralized socketManager instead of creating its own socket.
 * 
 * The invalidation system works as follows:
 * 1. Server emits 'invalidate' events when data changes
 * 2. This module receives those events via socketManager
 * 3. Registered listeners are notified to refresh their data
 * 
 * Usage:
 * ```ts
 * const unsubscribe = onInvalidate((payload) => {
 *   if (payload.type === 'clients') {
 *     refetchClients();
 *   }
 * });
 * // Later: unsubscribe();
 * ```
 */

import { socketManager } from './socketManager';

// Internal listener registry for invalidate events
const listeners = new Set<(payload: any) => void>();

// Track if we've subscribed to socketManager
let subscribed = false;

// Deduplication: Track recently processed events to prevent duplicate handling
// Key format: `${company}:${type}:${id}:${op}:${scope}:${managerId}:${room}:${nonce}`
// TTL: 200ms (small window to prevent true duplicates without blocking valid events)
const recentEvents = new Map<string, number>();
const DEDUP_WINDOW_MS = 200; // Reduced from 500ms to 200ms

/**
 * Generate deduplication key for an event
 * Includes all relevant fields to make events unique and scoped
 */
function getDedupKey(payload: any): string {
  // NEVER dedup events with empty payload - we cannot safely assume duplicates
  if (!payload || Object.keys(payload).length === 0) {
    // Generate unique key for empty payload to prevent dedup
    return `empty:${Date.now()}:${Math.random()}`;
  }
  
  const company = payload?.company || '';
  const type = payload?.type || '';
  // CRITICAL: Include leadId if present (for lead:owner-changed events)
  const id = payload?.id || payload?.entityId || payload?.leadId || '';
  const op = payload?.op || payload?.action || '';
  const scope = payload?.scope || 'all';
  const managerId = payload?.managerId || '';
  const room = payload?.room || payload?.targetRoom || '';
  // CRITICAL: Always include nonce if present (unique per mutation)
  const nonce = payload?.nonce || payload?.ts || '';
  
  // Build comprehensive key that includes all scoping information
  // CRITICAL: For owner change events, this ensures each mutation has a unique key (leadId + nonce)
  return `${company}:${type}:${id}:${op}:${scope}:${managerId}:${room}:${nonce}`;
}

/**
 * Check if event should be processed (not a duplicate)
 * 
 * IMPORTANT: This only blocks event processing if it's a true duplicate.
 * We still log the event even if deduped, but we don't emit it to listeners.
 */
function shouldProcessEvent(payload: any): boolean {
  // NEVER dedup events with empty payload
  if (!payload || Object.keys(payload).length === 0) {
    return true;
  }
  
  const key = getDedupKey(payload);
  const now = Date.now();
  const lastProcessed = recentEvents.get(key);
  
  if (lastProcessed && (now - lastProcessed) < DEDUP_WINDOW_MS) {
    // Duplicate event within dedup window - don't process
    return false;
  }
  
  // Mark as processed
  recentEvents.set(key, now);
  
  // Cleanup old entries (older than 2x dedup window)
  const cleanupThreshold = now - (DEDUP_WINDOW_MS * 2);
  for (const [k, timestamp] of recentEvents.entries()) {
    if (timestamp < cleanupThreshold) {
      recentEvents.delete(k);
    }
  }
  
  return true;
}

/**
 * Emit invalidate event to all registered listeners
 * Called internally when socketManager receives an 'invalidate' event
 */
export function emitInvalidate(payload: any): void {
  listeners.forEach((fn) => {
    try {
      fn(payload);
    } catch (err) {
      console.error('[live.ts] Error in invalidate listener:', err);
    }
  });
}

/**
 * Ensure we're subscribed to invalidation events from socketManager
 * This is called lazily when the first listener is registered
 */
function ensureSubscribed(): void {
  if (subscribed) return;
  
  // Subscribe to all invalidation-related events via socketManager
  const invalidateEvents = [
    'invalidate',
    'client:updated',  // Direct client update events
    'order:updated',   // Direct order update events
    'lead:updated',   // Direct lead update events
    'lead:owner-changed', // NEW: Lead owner change events (with ownerFrom/ownerTo)
    'agent:updated',  // Direct agent update events
    'task:updated',   // NEW: Direct task update events
    'tasks:changed',
    'calendar:changed',
    'clients:changed',
    'leads:changed',
    'orders:changed',
    'slots:changed',
    'appointments:changed',
    'reminders:changed',
    'workers:changed',
  ];

  invalidateEvents.forEach((event) => {
    socketManager.subscribe(event, (payload: any) => {
      // Normalize the payload based on event type
      let normalizedPayload: any;
      
      if (event === 'client:updated') {
        // client:updated events have structure: { company, client, op, at }
        normalizedPayload = {
          type: 'clients',
          op: payload?.op || 'update',
          id: payload?.client?._id || payload?.client?.id,
          data: payload?.client,
          company: payload?.company,
          at: payload?.at,
        };
      } else if (event === 'order:updated') {
        // order:updated events have structure: { orderId, order }
        normalizedPayload = {
          type: 'orders',
          op: 'update',
          id: payload?.orderId || payload?.order?._id,
          data: payload?.order,
          at: new Date().toISOString(),
        };
      } else if (event === 'lead:updated') {
        // lead:updated events have structure: { company, lead, leadId, op, managerFrom, managerTo, at }
        normalizedPayload = {
          type: 'leads',
          op: payload?.op || payload?.action || 'update',
          id: payload?.leadId || payload?.lead?._id,
          company: payload?.company,
          // Include managerFrom/managerTo if present (for owner change detection)
          ownerFrom: payload?.managerFrom || payload?.ownerFrom || [],
          ownerTo: payload?.managerTo || payload?.ownerTo || [],
          scope: (payload?.managerFrom || payload?.managerTo) ? 'owner-changed' : undefined,
          at: payload?.at || new Date().toISOString(),
        };
      } else if (event === 'lead:owner-changed') {
        // lead:owner-changed events have structure: { company, leadId, ownerFrom, ownerTo, op, ts, nonce, at }
        normalizedPayload = {
          type: 'leads',
          op: payload?.op || 'assigned',
          id: payload?.leadId,
          company: payload?.company,
          ownerFrom: payload?.ownerFrom || [], // Array of old manager IDs
          ownerTo: payload?.ownerTo || [],     // Array of new manager IDs
          scope: 'owner-changed', // Special scope to trigger both old and new manager refetches
          ts: payload?.ts,
          nonce: payload?.nonce,
          at: payload?.at || new Date().toISOString(),
        };
      } else if (event === 'agent:updated') {
        // agent:updated events have structure: { company, agentId, action, at }
        normalizedPayload = {
          type: 'agents',
          op: payload?.action || 'update',
          id: payload?.agentId,
          company: payload?.company,
          at: payload?.at || new Date().toISOString(),
        };
      } else if (event === 'task:updated') {
        // task:updated events have structure: { company, taskId, action, at }
        normalizedPayload = {
          type: 'tasks',
          op: payload?.action || 'update',
          id: payload?.taskId,
          company: payload?.company,
          at: payload?.at || new Date().toISOString(),
        };
      } else if (payload?.type) {
        // Already has type field
        normalizedPayload = payload;
      } else {
        // Derive type from event name
        normalizedPayload = { ...payload, type: event.replace(':changed', '') };
      }
      
      // Deduplicate before emitting
      if (shouldProcessEvent(normalizedPayload)) {
        emitInvalidate(normalizedPayload);
      }
    });
  });

  subscribed = true;
}

/**
 * Register a listener for invalidation events
 * 
 * @param fn - Callback function called when data is invalidated
 * @returns Unsubscribe function
 * 
 * @example
 * ```ts
 * useEffect(() => {
 *   const unsubscribe = onInvalidate((payload) => {
 *     if (payload.type === 'tasks') {
 *       refetch();
 *     }
 *   });
 *   return unsubscribe;
 * }, []);
 * ```
 */
export function onInvalidate(fn: (payload: any) => void): () => void {
  // Ensure we're subscribed to socketManager
  ensureSubscribed();
  
  // Add listener
  listeners.add(fn);
  
  // Return unsubscribe function
  return () => {
    listeners.delete(fn);
  };
}

/**
 * Ensure socket is connected
 * 
 * This is a compatibility function for existing code.
 * The actual connection is managed by SocketProvider.
 * This just ensures the invalidation subscription is set up.
 * 
 * @deprecated Use socketManager directly or rely on SocketProvider
 */
export function ensureLiveConnected(): void {
  ensureSubscribed();
}

/**
 * Register an external socket with the live system
 * 
 * @deprecated No longer needed - socketManager handles all socket connections
 */
export function registerLiveSocket(_socket: any): any {
  console.warn('[live] registerLiveSocket is deprecated - using centralized socketManager');
  ensureSubscribed();
  return _socket;
}